<template>
    <head>
        <title>{{ $t('campaign_version_one.meta_title') }}</title>
    </head>

    <CampaignLayout>
        <div class="relative min-h-[400px] sm:min-h-[440px] rounded-xl">
            <img
                src="/images/campaigns/cover.webp"
                srcset="
                    /images/campaigns/cover.webp    1x,
                    /images/campaigns/cover@2x.webp 2x
                "
                class="hidden sm:block w-full h-[440px] sm:rounded-xl object-cover"
                alt="Campaigns cover image"
            />

            <img
                src="/images/campaigns/cover-mobile.webp"
                srcset="
                    /images/campaigns/cover-mobile.webp    1x,
                    /images/campaigns/cover-mobile@2x.webp 2x
                "
                class="block sm:hidden w-full h-[410px] sm:rounded-xl object-cover"
                alt="Campaigns cover image"
            />

            <div
                class="absolute bottom-0 sm:-bottom-[25%] left-0 right-0 mx-auto grid min-h-[240px] w-[95%] sm:w-[70%] lg:w-[85%] grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 rounded-md sm:rounded-xl bg-white p-[17px] sm:py-[35px] sm:px-[25px] min-[1180px]:px-[50px] shadow-lg"
            >
                <div>
                    <h2
                        class="hidden sm:block mb-4 text-[28px] font-bold leading-9 text-[#2C2C4F]"
                    >
                        {{ $t("campaign_version_one.slider_title") }}
                    </h2>

                    <h2
                        class="block sm:hidden mb-3 text-[22px] font-bold leading-9 text-[#2C2C4F]"
                    >
                        {{ $t("campaign_version_one.slider_mobile_title") }}
                    </h2>

                    <SliderCheckListItem
                        v-for="(sliderItem, index) in sliderChecklist"
                        :key="`slider-checklistitem-${index}`">
                        <p v-html="sliderItem"></p>
                    </SliderCheckListItem>
                </div>

                <div
                    class="flex flex-col items-center justify-center bg-white duration-150"
                    :class="{'fixed z-50 pb-3 top-0 left-0 right-0 shadow-md': stickySliderElem}">
                    <div class="w-full h-8 p-5">
                        <v-slider
                            v-model="annualConsumption"
                            color="#108FE5"
                            track-color="#DFDFE2"
                            thumb-color="white"
                            thumb-size="30"
                            track-size="6"
                            min="2000"
                            max="10000"
                            step="100"
                            rounded
                        />
                    </div>

                    <p class="mt-4 leading-7 text-[#2C2C4F]">
                        <span class="text-[24px] font-bold">
                            {{ isAnnualConsumptionUnknown ?
                                $numberHelper.formatMoney(annualConsumption * polandElectricityRatePerkWh) :
                                annualConsumption
                            }}
                        </span>

                        <span class="text-[18px] font-semibold pl-1">
                            {{ isAnnualConsumptionUnknown ? $t("campaign_version_one.pln-per-month") : 'kWh' }}
                        </span>
                    </p>

                    <p
                        v-if="isAnnualConsumptionUnknown"
                        class="text-sm leading-normal text-gray-400"
                        :class="{'hidden': stickySliderElem}">
                        {{ $t("campaign_version_one.Average electricity bill") }}
                    </p>

                    <p
                        v-else
                        class="text-sm leading-normal text-gray-400"
                        :class="{'hidden': stickySliderElem}">
                        {{ $t("campaign_version_one.slider_annual_consumption") }}
                    </p>

                    <div
                        v-if="locale === 'pl' && !stickySliderElem"
                        class="mt-4 flex justify-start items-center">
                        <div class="flex items-center justify-center">
                            <label class="flex items-center cursor-pointer">
                                <div class="relative">
                                    <input
                                        type="checkbox"
                                        v-model="isAnnualConsumptionUnknown"
                                        class="sr-only"
                                    />

                                    <div
                                        class="block w-[40px] h-[24px] rounded-full"
                                        :class="{'bg-[#108FE5]': isAnnualConsumptionUnknown, 'bg-[#CFD6EA]': !isAnnualConsumptionUnknown}"
                                    />

                                    <div
                                        class="absolute left-[3px] top-[3px] bg-white w-[18px] h-[18px] rounded-full transition"
                                        :class="{'transform translate-x-[90%]': isAnnualConsumptionUnknown}"
                                    />

                                </div>
                            </label>
                        </div>

                        <p
                            class="text-[14px] leading-snug ml-3"
                            :class="{'text-[#12122F]': isAnnualConsumptionUnknown, 'text-[#959BB5]': !isAnnualConsumptionUnknown}">
                            {{ $t("campaign_version_one.slider_annual_consumption_checkbox") }}
                        </p>
                    </div>

                    <v-fade-transition leave-absolute>
                        <div class="block sm:hidden">
                            <v-btn
                                v-if="mobileShowOfferBtn && !stickySliderElem"
                                color="blue-darken-3"
                                variant="tonal"
                                size="small"
                                class="mt-4"
                                @click="scrollToElement('quotesContainer')">
                                {{ $t("campaign_version_one.See your offer below") }}
                            </v-btn>
                        </div>
                    </v-fade-transition>
                </div>
            </div>
        </div>

        <!-- PROMO BANNER -->
        <div ref="promoBannerParent" class="w-[95%] sm:w-[70%] lg:w-[85%] mt-[40px] sm:mt-[150px] mx-auto">
            <PromoBanner v-if="discount > 0">
                <template v-slot:title>
                    <p
                        v-html="$t('campaign_version_one.promo_title', {
                            amount: parseInt(discount) + priceSuffix,
                            dueDate: discountExpirationDate
                        })"
                        class="hidden lg:block"
                    />

                    <p
                        v-html="$t('campaign_version_one.promo_title_mobile', {
                            amount: parseInt(discount),
                            dueDate: discountExpirationDate
                        })"
                        class="block lg:hidden"
                    />
                </template>

                <p v-html="$t('campaign_version_one.promo_description', {
                    amount: parseInt(discount),
                    date: discountExpirationDate
                })" />
            </PromoBanner>
        </div>

        <!-- QUOTES -->
        <div
            ref="quotesContainer"
            class="mx-auto mt-[30px] grid w-[95%] sm:w-[70%] lg:w-[85%] grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12"
        >
            <quote
                v-for="(quote, index) in quotes"
                :key="`quote-${index}`"
                :is-popular="quote.isPopular"
                :title="quote.title"
                :description="quote.description"
                :locale="locale"
                :quote-pv-model-data="quotePvModelData"
                :all-inverters="quote.allInverters"
                :solar-panel-id="quote.solarPanelId"
                :annual-consumption="annualConsumption"
                :discount="discount"
                :user-extra-premium="userExtraPremium"
            />
        </div>

        <!-- REVIEWS CAROUSEL -->
        <div class="mx-auto mt-[50px] w-[95%] sm:w-[70%] lg:w-[85%]">
            <ReviewsCarousel :reviews="reviews" />
        </div>

        <!-- FAQ -->
        <div class="mx-auto mt-[50px] w-[95%] sm:w-[70%] lg:w-[85%]">
            <p class="faq-title text-[22px] sm:text-[28px] font-bold sm:leading-loose text-[#2C2C4F]">
                {{ $t("campaign_version_one.Frequently Asked Questions") }}
            </p>

            <div class="mt-[38px] grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-y-6 lg:gap-x-12">
                <FaqItem v-for="faq in faqs" :key="faq.title">
                    <template v-slot:title>
                        {{ faq.title }}
                    </template>

                    {{ faq.content }}
                </FaqItem>
            </div>
        </div>
    </CampaignLayout>
</template>

<script>
import CampaignLayout from "@/Layouts/Campaign";
import SliderCheckListItem from "@/Components/Campaigns/SliderCheckListItem";
import Quote from "@/Components/Campaigns/Quote";
import ReviewsCarousel from "@/Components/Campaigns/ReviewsCarousel";
import FaqItem from "@/Components/Campaigns/FaqItem";
import PromoBanner from "@/Components/Campaigns/PromoBanner";
import { Head } from "@inertiajs/inertia-vue3";

export default {
    props: {
        locale: {
            type: String,
            required: true,
        },
        roofSheathings: {
            type: Array,
            required: true,
        },
        allSolarPanels: {
            type: Array,
            required: true,
        },
        allCheaperInverters: {
            type: Array,
            required: true,
        },
        allPremiumInverters: {
            type: Array,
            required: true,
        },
        allBatteries: {
            type: Array,
            required: true,
        },
        priceFactors: {
            type: Array,
            required: true,
        },
        minimumCompanyMargins: {
            type: Array,
            required: true,
        },
        baseMargins: {
            type: Array,
            required: true,
        },
        showOffers: {
            type: Boolean,
            required: true,
        },
        cheaperSolarPanelId: {
            type: Number,
            required: true,
        },
        premiumSolarPanelId: {
            type: Number,
            required: true,
        },
        errors: {
            type: Object,
            required: false,
        },
        auth: {
            type: Object,
            required: false,
        },
        toast: {
            type: Object,
            required: false,
        },
        discount: {
            type: Number,
            required: false,
            default: () => 0,
        },
        userExtraPremium: {
            type: Number,
            required: false,
            default: () => 0,
        },
        discountExpirationDate: {
            type: String,
            required: true,
        },
    },
    components: {
        CampaignLayout,
        SliderCheckListItem,
        Quote,
        ReviewsCarousel,
        FaqItem,
        PromoBanner,
    },
    computed: {
        priceSuffix() {
            return this.locale === 'pl' ? ' zł ' : '';
        }
    },
    created() {
        window.addEventListener('scroll', this.stickySlider);
        window.addEventListener('resize', this.stickySlider);
    },
    destroyed() {
        window.removeEventListener('scroll', this.stickySlider);
        window.removeEventListener('resize', this.stickySlider);
    },
    mounted() {
        this.stickySlider();
    },
    data() {
        return {
            annualConsumption: 6000,
            polandElectricityRatePerkWh: 0.182,
            sliderChecklist: [
                this.$t('campaign_version_one.slider_check_list_item_1'),
                this.$t('campaign_version_one.slider_check_list_item_2'),
                this.$t('campaign_version_one.slider_check_list_item_3'),
            ],
            isAnnualConsumptionUnknown: false,
            mobileShowOfferBtn: false,
            quotes: [
                {
                    isPopular: true,
                    title: this.$t('campaign_version_one.popular_quote_title'),
                    description: this.$t('campaign_version_one.popular_quote_descrition'),
                    allInverters: this.allCheaperInverters,
                    solarPanelId: this.cheaperSolarPanelId,
                },
                {
                    isPopular: false,
                    title: this.$t('campaign_version_one.quote_title'),
                    description: this.$t('campaign_version_one.quote_descrition'),
                    allInverters: this.allPremiumInverters,
                    solarPanelId: this.premiumSolarPanelId,
                }
            ],
            quotePvModelData: {
                roofSheathings: this.roofSheathings,
                allSolarPanels: this.allSolarPanels,
                allBatteries: this.allBatteries,
                priceFactors: this.priceFactors,
                minimumCompanyMargins: this.minimumCompanyMargins,
                baseMargins: this.baseMargins,

            },
            reviews: [
                {
                    name: this.$t('campaign_version_one.reviews_item_1.name'),
                    date: this.$t('campaign_version_one.reviews_item_1.date'),
                    content: this.$t('campaign_version_one.reviews_item_1.content'),
                    imageSrc: `/images/campaigns/version-one/reviews/${this.locale}_item_1.webp`,
                },
                {
                    name: this.$t('campaign_version_one.reviews_item_2.name'),
                    date: this.$t('campaign_version_one.reviews_item_2.date'),
                    content: this.$t('campaign_version_one.reviews_item_2.content'),
                    imageSrc: `/images/campaigns/version-one/reviews/${this.locale}_item_2.webp`,
                },
                {
                    name: this.$t('campaign_version_one.reviews_item_3.name'),
                    date: this.$t('campaign_version_one.reviews_item_3.date'),
                    content: this.$t('campaign_version_one.reviews_item_3.content'),
                    imageSrc: `/images/campaigns/version-one/reviews/${this.locale}_item_3.webp`,
                },
            ],
            faqs: [
                {
                    title: this.$t('campaign_version_one.faq_title_item_1'),
                    content: this.$t('campaign_version_one.faq_content_item_1'),
                },
                {
                    title: this.$t('campaign_version_one.faq_title_item_2'),
                    content: this.$t('campaign_version_one.faq_content_item_2'),
                },
                {
                    title: this.$t('campaign_version_one.faq_title_item_3'),
                    content: this.$t('campaign_version_one.faq_content_item_3'),
                },
                {
                    title: this.$t('campaign_version_one.faq_title_item_4'),
                    content: this.$t('campaign_version_one.faq_content_item_4'),
                },
                {
                    title: this.$t('campaign_version_one.faq_title_item_5'),
                    content: this.$t('campaign_version_one.faq_content_item_5'),
                },
                {
                    title: this.$t('campaign_version_one.faq_title_item_6'),
                    content: this.$t('campaign_version_one.faq_content_item_6'),
                },
            ],
            stickySliderElem: false,
        };
    },
    watch: {
        annualConsumption() {
            this.mobileShowOfferBtn = true
        },
    },
    methods: {
        scrollToElement(element) {
            this.$nextTick(() => {
                this.$refs[element].scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            });

            this.mobileShowOfferBtn = false;
        },
        stickySlider() {
            const top = window.pageYOffset || document.documentElement.scrollTop;
            const detectScroll = this.$refs.promoBannerParent.offsetTop - 40;
            const windowWidth = window.innerWidth;

            if (windowWidth < 640 && top - detectScroll > 0) {
                this.stickySliderElem = true;

                return;
            }

            this.stickySliderElem = false;
        },
    },
};
</script>

<style>
.v-slider-thumb__surface {
    border: solid 1px #E3E2E2 !important;
}

.faq-title {
    text-align: left;
}

@media screen and (min-width: 640px) {
    .faq-title {
        text-align: center;
    }
}
</style>
